import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Upload, Loader2, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const empty = () => ({
  full_name: "",
  date_of_birth: "",
  address: "",
  country: "",
  id_type: "passport",
  id_front_url: "",
  id_back_url: "",
  selfie_url: "",
  proof_of_address_url: "",
  nok_full_name: "",
  nok_relationship: "",
  nok_phone: "",
  nok_email: "",
  nok_address: "",
});

export default function KYC() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty());
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/kyc/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user?.kyc_status]);

  if (!status) return <div className="p-8 text-gray-500">Loading...</div>;

  const kyc = status.status;

  if (kyc === "pending") return <KYCStatusCard icon={Clock} color="#F59E0B" title="Under review" subtitle="Our team is verifying your documents. This usually takes a few minutes to a few hours." onBack={() => navigate("/dashboard")} />;

  if (kyc === "approved")
    return <KYCStatusCard icon={CheckCircle2} color="#10B981" title="You're verified" subtitle="You have full access to buy bricks. Browse properties now." onBack={() => navigate("/properties")} backLabel="Browse properties" />;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    const req = ["full_name", "date_of_birth", "address", "country", "id_type", "id_front_url", "selfie_url", "nok_full_name", "nok_relationship", "nok_phone"];
    const miss = req.filter((k) => !form[k]);
    if (miss.length) {
      toast.error(`Missing: ${miss.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("Submitted — check back soon");
      await refreshUser();
      const { data } = await api.get("/kyc/status");
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
        <div className="overline">Identity verification</div>
      </div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mb-3">
        Verify your identity
      </h1>
      <p className="text-gray-400 max-w-2xl mb-8">
        To comply with global anti-money-laundering regulations, every investor must complete a one-time identity check before buying bricks.
        Your documents are stored securely and reviewed by our compliance team.
      </p>

      {kyc === "rejected" && status.rejection_reason && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex gap-3">
          <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-red-300 font-medium">Previous submission rejected</div>
            <div className="text-sm text-gray-400 mt-1">{status.rejection_reason}</div>
            <div className="text-xs text-gray-500 mt-2">Please resubmit with corrected documents.</div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bp-card rounded-2xl p-6 md:p-8 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full legal name">
            <Input data-testid="kyc-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
          </Field>
          <Field label="Date of birth">
            <Input data-testid="kyc-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
          </Field>
          <Field label="Country">
            <Input data-testid="kyc-country" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="United Kingdom" className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
          </Field>
          <Field label="ID type">
            <Select value={form.id_type} onValueChange={(v) => set("id_type", v)}>
              <SelectTrigger data-testid="kyc-id-type" className="bg-[#050914] border-white/10 focus:border-[#D4AF37]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] text-white border-white/10">
                <SelectItem value="passport">Passport</SelectItem>
                <SelectItem value="national_id">National ID</SelectItem>
                <SelectItem value="drivers_license">Driver's licence</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Residential address">
              <Textarea data-testid="kyc-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
            </Field>
          </div>
        </div>

        <div className="pt-5 border-t border-white/5">
          <div className="overline mb-4">Documents</div>
          <div className="grid md:grid-cols-2 gap-4">
            <FileField label="ID — front" testid="kyc-id-front" value={form.id_front_url} onChange={(v) => set("id_front_url", v)} required />
            <FileField label="ID — back (if applicable)" testid="kyc-id-back" value={form.id_back_url} onChange={(v) => set("id_back_url", v)} />
            <FileField label="Selfie holding ID" testid="kyc-selfie" value={form.selfie_url} onChange={(v) => set("selfie_url", v)} required />
            <FileField label="Proof of address (optional)" testid="kyc-poa" value={form.proof_of_address_url} onChange={(v) => set("proof_of_address_url", v)} />
          </div>
        </div>

        <div className="pt-5 border-t border-white/5">
          <div className="overline mb-1">Next of kin</div>
          <p className="text-xs text-gray-500 mb-4">In the unlikely event of incapacitation, this person will be contacted regarding your holdings.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name">
              <Input data-testid="kyc-nok-name" value={form.nok_full_name} onChange={(e) => set("nok_full_name", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
            </Field>
            <Field label="Relationship">
              <Input data-testid="kyc-nok-relationship" placeholder="Spouse, Parent, Sibling..." value={form.nok_relationship} onChange={(e) => set("nok_relationship", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
            </Field>
            <Field label="Phone number">
              <Input data-testid="kyc-nok-phone" value={form.nok_phone} onChange={(e) => set("nok_phone", e.target.value)} placeholder="+234..." className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
            </Field>
            <Field label="Email (optional)">
              <Input data-testid="kyc-nok-email" type="email" value={form.nok_email} onChange={(e) => set("nok_email", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Address (optional)">
                <Textarea rows={2} value={form.nok_address} onChange={(e) => set("nok_address", e.target.value)} className="bg-[#050914] border-white/10 focus:border-[#D4AF37]" />
              </Field>
            </div>
          </div>
        </div>

        <Button type="submit" disabled={busy} className="btn-gold rounded-full h-12 px-10" data-testid="kyc-submit">
          {busy ? "Submitting..." : "Submit for review"}
        </Button>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          By submitting, you confirm that all information is true and accurate. We use your data solely for compliance purposes and never share it with third parties.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FileField({ label, value, onChange, testid, required }) {
  const [uploading, setUploading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${backend}${data.url}`);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-gray-300 text-xs uppercase tracking-wider">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </Label>
      <div className="mt-2 flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="" className="w-20 h-20 rounded-lg object-cover border border-white/10" />
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg border border-dashed border-white/10 bg-[#050914] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-gray-600" />
          </div>
        )}
        <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs cursor-pointer hover:bg-[#D4AF37]/10 transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? "Uploading..." : value ? "Replace" : "Upload"}
          <input type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid={testid} />
        </label>
      </div>
    </div>
  );
}

function KYCStatusCard({ icon: Icon, color, title, subtitle, onBack, backLabel = "Back to dashboard" }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <div className="bp-card rounded-2xl p-10">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-6" style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <h2 className="font-heading text-3xl text-white font-light tracking-tight" data-testid="kyc-status-title">{title}</h2>
        <p className="text-gray-400 mt-3 text-sm leading-relaxed">{subtitle}</p>
        <Button onClick={onBack} className="btn-gold rounded-full mt-8 px-8" data-testid="kyc-status-back">
          {backLabel}
        </Button>
      </div>
    </div>
  );
}
